import { Injectable } from '@angular/core';
import { Subject } from 'rxjs';
import { SurveyUserTypes } from '../../../../constants-types';
import {
    Folder,
    FolderUpdate,
    OpenInvitationLinkType,
    OpenInvitationPatch,
    OpenInvitationPost,
    OpenInvitationServerEntry,
    OpenInvitationUpdate
} from '../../../../core-data-model';
import {
    FoldersDataService,
    notificationOnError
} from '../../../../core-services';
import {
    ApiService,
    UserProfileSettingsService
} from '../../../../system-services';
import { IOpenInvitationLink } from './IOpenInvitationLink.interface';

@Injectable({ providedIn: 'root' })
export class PlatformOpenInvitationService {
    editLink: Subject<IOpenInvitationLink> = new Subject();
    reloadTable: Subject<boolean> = new Subject();

    constructor(
        private userProfile: UserProfileSettingsService,
        private api: ApiService,
        private folders: FoldersDataService
    ) {}

    @notificationOnError('', 'sellertools.oi.folderError')
    createOIFolder(linkName: string, parentId: string): Promise<Folder> {
        const folder = new FolderUpdate({
            name: `${linkName} Responses`,
            parentId
        });
        return this.folders.updateFolder(folder);
    }

    fetchAllOILinks(): Promise<IOpenInvitationLink[]> {
        return this.api
            .get<
                OpenInvitationServerEntry[]
            >(`/api/v4/companies/${this.userProfile.currentCompany}/openinvitations`)
            .then(([server]) =>
                server
                    ? server.map(
                          (oi) =>
                              ({
                                  id: oi.id,
                                  modifiedTime: oi.modifiedTime,
                                  name: oi.linkName,
                                  description: oi.description,
                                  folderId: oi.folderId,
                                  url: oi.linkUrl,
                                  kiosk: oi.isKioskMode,
                                  boomerang:
                                      oi.emailNotificationsSetting
                                          .includeBAReportInAssessmentTakerEmail,
                                  language: oi.languageCode,
                                  redirectURL: oi.redirectUrl,
                                  stakeholders:
                                      oi.emailNotificationsSetting
                                          .stakeholderEmails,
                                  linkOwner: {
                                      displayName: `${oi.linkContactInfo.firstName} ${oi.linkContactInfo.firstName}`,
                                      value: oi.linkContactInfo.email
                                  },
                                  linkType: oi.linkType,
                                  notificationSettings: {
                                      notifyAssessmentTaker:
                                          oi.emailNotificationsSetting
                                              .notifyAssessmentTaker,
                                      notifyAssessmentSender:
                                          oi.emailNotificationsSetting
                                              .notifyAssessmentSender,
                                      notifyStakeholders:
                                          oi.emailNotificationsSetting
                                              .notifyStakeholders,
                                      includeBAReportInStakeholderEmail:
                                          oi.emailNotificationsSetting
                                              .includeBAReportInStakeholderEmail,
                                      includeBAReportInAssessmentTakerEmail:
                                          oi.emailNotificationsSetting
                                              .includeBAReportInAssessmentTakerEmail,
                                      includeBAReportInLinkSenderEmail:
                                          oi.emailNotificationsSetting
                                              .includeBAReportInLinkSenderEmail
                                  }
                              }) as IOpenInvitationLink
                      )
                    : []
            );
    }

    @notificationOnError()
    createOpenInvitation(openInvitation: OpenInvitationUpdate): Promise<any> {
        return this.api.post<OpenInvitationPost, OpenInvitationServerEntry>(
            `/api/v4/companies/${this.userProfile.currentCompany}/openinvitations`,
            this.clientToServerForPost(openInvitation)
        );
    }

    @notificationOnError()
    updateOpenInvitation(
        openInvitation: OpenInvitationUpdate,
        id: string
    ): Promise<any> {
        return this.api.patch<OpenInvitationPatch, OpenInvitationServerEntry>(
            `/api/v4/companies/${this.userProfile.currentCompany}/openinvitations/${id}`,
            this.clientToServerForPatch(openInvitation)
        );
    }

    clientToServerForPost(
        openInvitation: OpenInvitationUpdate
    ): OpenInvitationPost {
        return {
            linkName: openInvitation.linkName,
            description: openInvitation.description,
            folderId: openInvitation.folder.id,
            jobId: null,
            languageCode: openInvitation.language.locale,
            linkContactInfo: {
                firstName: openInvitation.ownerFirstName,
                lastName: openInvitation.ownerLastName,
                email: openInvitation.ownerEmail
            },
            personType: SurveyUserTypes[openInvitation.personType],
            emailNotificationsSetting: {
                notifyAssessmentTaker: openInvitation.notifyAssessmentTaker,
                includeBAReportInAssessmentTakerEmail:
                    openInvitation.includeBAReportInAssessmentTakerEmail,
                includeSnapshotInAssessmentTakerEmail: false,
                notifyAssessmentSender: openInvitation.notifyAssessmentSender,
                includeBAReportInLinkSenderEmail:
                    openInvitation.includeBAReportInLinkSenderEmail,
                stakeholderEmails: openInvitation.stakeholderEmails ?? '',
                notifyStakeholders: openInvitation.notifyStakeholders,
                includeBAReportInStakeholderEmail:
                    openInvitation.includeBAReportInStakeholderEmail,
                stakeholderNotifications:
                    openInvitation.stakeholderNotifications ?? []
            },
            isKioskMode: openInvitation.kioskMode,
            redirectUrl: openInvitation.redirectUrl ?? null,
            webhookUrl: '',
            sendCognitiveAssessmentAfterBA: false,
            isMigrated: false,
            oi3Version: '2.0',
            linkType: OpenInvitationLinkType.SellerLink
        };
    }

    clientToServerForPatch(
        openInvitation: OpenInvitationUpdate
    ): OpenInvitationPatch {
        const returnObj: OpenInvitationPatch = {
            description: openInvitation.description,
            folderId: openInvitation.folder.id,
            languageCode: openInvitation.language.locale,
            linkContactInfo: {
                firstName: openInvitation.ownerFirstName,
                lastName: openInvitation.ownerLastName,
                email: openInvitation.ownerEmail
            },
            personType: SurveyUserTypes[openInvitation.personType],
            notifyAssessmentTaker: openInvitation.notifyAssessmentTaker,
            includeBAReportInAssessmentTakerEmail:
                openInvitation.includeBAReportInAssessmentTakerEmail,
            notifyAssessmentSender: openInvitation.notifyAssessmentSender,
            includeBAReportInLinkSenderEmail:
                openInvitation.includeBAReportInLinkSenderEmail,
            stakeholderEmails: openInvitation.stakeholderEmails ?? '',
            notifyStakeholders: openInvitation.notifyStakeholders,
            includeBAReportInStakeholderEmail:
                openInvitation.includeBAReportInStakeholderEmail,
            isKioskMode: openInvitation.kioskMode,
            redirectUrl: openInvitation.redirectUrl ?? null,
            isMigrated: false,
            oi3Version: '2.0',
            linkType: OpenInvitationLinkType.SellerLink,
            emailNotificationsSetting: {
                notifyAssessmentTaker: openInvitation.notifyAssessmentTaker,
                includeBAReportInAssessmentTakerEmail:
                    openInvitation.includeBAReportInAssessmentTakerEmail,
                includeSnapshotInAssessmentTakerEmail: false,
                notifyAssessmentSender: openInvitation.notifyAssessmentSender,
                includeBAReportInLinkSenderEmail:
                    openInvitation.includeBAReportInLinkSenderEmail,
                stakeholderEmails: openInvitation.stakeholderEmails ?? '',
                notifyStakeholders: openInvitation.notifyStakeholders,
                includeBAReportInStakeholderEmail:
                    openInvitation.includeBAReportInStakeholderEmail,
                stakeholderNotifications:
                    openInvitation.stakeholderNotifications ?? []
            }
        };
        // Note that if job is null it is cleared via a different API Call
        if (openInvitation.job && !openInvitation.job.isUnknown()) {
            returnObj.jobId = openInvitation.job.id;
        }

        if (
            openInvitation.originalObject.linkName !== openInvitation.linkName
        ) {
            returnObj.linkName = openInvitation.linkName;
        }
        return returnObj;
    }

    async deleteOiLink(id: string): Promise<any> {
        await this.api.delete(
            `/api/v4/companies/${this.userProfile.currentCompany}/openinvitations/${id}`
        );
        this.reloadTable.next(true);
    }
}
